/**
 * The six agent→renderer messages of A2UI v1.0, and our transport envelope.
 *
 * **Transcribed from the vendored schemas rather than generated, and that is a
 * deliberate departure from the plan.** The catalogue earns a generator: 18
 * components, authored from pydantic on the backend, changing whenever a card
 * changes. These six shapes are the PROTOCOL — pinned by a sha in
 * `graphs/peterhof_canvas/a2ui_schemas/*.sha256`, changing only on a spec
 * version bump — and they are written in the parts of JSON Schema a small
 * generator handles worst (`allOf`, cross-file `$ref`, `unevaluatedProperties`).
 * A second generator for them would be more code than it emits, and more likely
 * to be wrong. What replaces it is evidence: `MessageProcessor` is driven by the
 * backend's own golden fixtures, and every message the backend emits is
 * validated against the official schemas in ITS tests (`test_pc_catalog.py`).
 * So the wire is pinned by the spec on one side and by the same bytes on the
 * other.
 *
 * Source: `agent_to_renderer.json` ($id .../v1_0/agent_to_renderer.json),
 * `agent_to_renderer_list_wrapper.json`, `common_types.json`.
 */

/** `common_types.json#/$defs/ComponentId`. */
export type ComponentId = string;

/**
 * `common_types.json#/$defs/ChildList` — a static list of ids, or a template
 * over the data model. We only emit the first; the second is part of the
 * protocol and a renderer that refuses a legal message is the one that is wrong.
 */
export type ChildList = ComponentId[] | { componentId: ComponentId; path: string };

/**
 * One node: the common envelope plus whatever the catalogue declares for its
 * `component`. Deliberately loose here — the catalogue's generated zod schemas
 * are what give it a shape, and they do it per component.
 */
export type A2uiComponent = {
  id: ComponentId;
  component: string;
  catalogId?: string;
  children?: ChildList;
  [prop: string]: unknown;
};

export type CreateSurfaceMessage = {
  version: 'v1.0';
  createSurface: {
    surfaceId: string;
    catalogId?: string;
    sendDataModel?: boolean;
    components?: A2uiComponent[];
    dataModel?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
  };
};

export type UpdateComponentsMessage = {
  version: 'v1.0';
  updateComponents: {
    surfaceId: string;
    components: A2uiComponent[];
  };
};

/**
 * **ONE path and ONE value — there is no `updates` array in v1.0.** The design
 * document showed a list; the vendored schema requires `surfaceId` and `value`
 * and takes a single optional `path`. Deleting is `value: null`, and an omitted
 * path (or `/`) means the whole model.
 */
export type UpdateDataModelMessage = {
  version: 'v1.0';
  updateDataModel: {
    surfaceId: string;
    path?: string;
    value: unknown;
  };
};

export type DeleteSurfaceMessage = {
  version: 'v1.0';
  deleteSurface: { surfaceId: string };
};

export type CallRendererFunctionMessage = {
  version: 'v1.0';
  callRendererFunction: {
    functionCallId: string;
    callFunction: { call: string; catalogId: string; args?: Record<string, unknown> };
  };
};

export type AgentFunctionResponseMessage = {
  version: 'v1.0';
  agentFunctionResponse: {
    functionCallId: string;
    value?: unknown;
    error?: unknown;
  };
};

export type AgentToRenderer =
  | CreateSurfaceMessage
  | UpdateComponentsMessage
  | UpdateDataModelMessage
  | DeleteSurfaceMessage
  | CallRendererFunctionMessage
  | AgentFunctionResponseMessage;

/** `agent_to_renderer_list_wrapper.json` — the key really is `messages`. */
export type AgentToRendererList = { messages: AgentToRenderer[] };

/**
 * Our transport envelope, and the ONLY place `seq`/`rev` live.
 *
 * The spec leaves ordering, framing and metadata to the transport, so these two
 * are ours and stay OUT of the A2UI payload: `seq` deduplicates a frame replayed
 * by `Last-Event-ID`, `rev` settles the race between a `createSurface` built
 * from `GET /state` and live frames that overtook it.
 */
export type DhUiEnvelope = {
  dh_ui: {
    kind: 'a2ui';
    v: number;
    seq: number;
    rev: number;
    payload: AgentToRendererList;
  };
};

// ── narrow readers ─────────────────────────────────────────────────────────
// Type predicates rather than a `kind` field: an A2UI message is discriminated
// by WHICH key it carries, which is how the schema's `oneOf` reads too.

const has = (value: unknown, key: string): boolean =>
  typeof value === 'object' && value !== null && key in (value as Record<string, unknown>);

export const isCreateSurface = (m: AgentToRenderer): m is CreateSurfaceMessage =>
  has(m, 'createSurface');
export const isUpdateComponents = (m: AgentToRenderer): m is UpdateComponentsMessage =>
  has(m, 'updateComponents');
export const isUpdateDataModel = (m: AgentToRenderer): m is UpdateDataModelMessage =>
  has(m, 'updateDataModel');
export const isDeleteSurface = (m: AgentToRenderer): m is DeleteSurfaceMessage =>
  has(m, 'deleteSurface');

/** The name of whichever message this is — for logs and the ignore path. */
export function messageKind(message: unknown): string {
  if (typeof message !== 'object' || message === null) return 'not-an-object';
  for (const key of [
    'createSurface',
    'updateComponents',
    'updateDataModel',
    'deleteSurface',
    'callRendererFunction',
    'agentFunctionResponse',
  ]) {
    if (key in (message as Record<string, unknown>)) return key;
  }
  return 'unknown';
}

/**
 * Reads our envelope off a `custom` frame's data, tolerating the wrapper the
 * older reagent put around it. Returns `null` when this is not an `a2ui` frame
 * — a `run_meta` frame comes down the same channel.
 */
export function readEnvelope(data: unknown): DhUiEnvelope['dh_ui'] | null {
  if (typeof data !== 'object' || data === null) return null;
  const record = data as Record<string, unknown>;
  const inner = (record.dh_ui ?? (record.chunk as Record<string, unknown>)?.dh_ui) as
    | Record<string, unknown>
    | undefined;
  if (!inner || inner.kind !== 'a2ui') return null;
  const payload = inner.payload as { messages?: unknown } | undefined;
  if (!payload || !Array.isArray(payload.messages)) return null;
  return {
    kind: 'a2ui',
    v: typeof inner.v === 'number' ? inner.v : 1,
    seq: typeof inner.seq === 'number' ? inner.seq : 0,
    rev: typeof inner.rev === 'number' ? inner.rev : 0,
    payload: { messages: payload.messages as AgentToRenderer[] },
  };
}
